import { useState, useEffect } from 'react'
import { assignWith, isEmpty } from 'lodash'
import request from '@/modules/base/util/request'

import settingsConfig from '@/modules/settings/config'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'

const DEFAULT_BILLING_DATA = {
  // DO-13587: the salutation is optional, "Keine Angabe" is the default
  salutation: '',
  title: '',
  bankname: '',
  bankAccountBic: '',
  bankAccountHolder: '',
  bankAccountIBAN: '',
  firstName: '',
  lastName: '',
  companyName: '',
  street: '',
  zipCode: '',
  sepaMandate: false,
  city: '',
  countryIso: 'DE',
  vatNumber: '',
}

// the fields behind "Aus Deinem Konto übernommen"
const ADDRESS_FIELDS = ['companyName', 'street', 'zipCode', 'city'] as const

const bankDetailFields = [
  'bankAccountHolder',
  'bankAccountIBAN',
  'sepaMandate',
]

/**
 * Whether the billing form allows the purchase. Bank details are only required when something
 * is paid - the caller derives it from the cart, so a switch between a FREE and a paid plan
 * re-evaluates it without any input in the form.
 */
export const isBillingDataValid = (data: Record<string, unknown>, withoutBankDetails: boolean, ibanValid: boolean) => {
  let billingFormFields: string[] = settingsConfig.billingFormFields
  if (withoutBankDetails) {
    billingFormFields = billingFormFields.filter(field => !bankDetailFields.includes(field))
  }

  if (data.countryIso === 'DE') {
    billingFormFields = billingFormFields.filter(field => field !== 'vatNumber')
  }

  const invalidInputs = billingFormFields.filter((prop) => !data[prop])

  return invalidInputs.length <= 0 && (withoutBankDetails || ibanValid)
}

export default function useBillingForm(props) {
  const { activeSubscriptions } = props

  const [billingFormData, setBillingFormData] = useState({})
  const [isIBANValid, setIsIBANValid] = useState(false)
  // the address was filled in from the account or the current subscription - an account without
  // company data starts with empty fields, then the checkout does not claim to have taken anything over
  const [isFromAccount, setIsFromAccount] = useState(false)

  const initSetup = async() => {
    const activeSubscription = activeSubscriptions.find(sub => !isEmpty(sub.billingData))

    try {
      const accountResponse = await request(settingsConfig.account, { auth: true, isConcurrent: true })
      const {
        body: {
          data: {
            accountEmail,
            companyAddress,
            ownerFirstName: firstName,
            ownerLastName: lastName,
            bankAccountHolder: accountHolder,
            bankAccountIBAN: accountIBAN,
            bankAccountBic,
            bankname,
          },
        },
      } = accountResponse

      const defaultBillingData = {
        ...DEFAULT_BILLING_DATA,
        ...companyAddress,
        email: accountEmail,
        firstName,
        lastName,
        companyName: companyAddress.companyName,
        city: companyAddress.city,
        countryIso: companyAddress.countryIso,
        zipCode: companyAddress.zipCode,
        street: companyAddress.street,
        bankAccountHolder: accountHolder,
        bankAccountIBAN: accountIBAN,
        bankAccountBic,
        bankname,
      }

      if (activeSubscription) {
        const billingDataResponse = await request(settingsConfig.getSubscriptionBillingData(activeSubscription.id), { auth: true })
        const {
          body: {
            data: {
              bankAccountIBAN,
              bankAccountHolder,
              city,
              countryIso,
              zipCode,
              street,
              firstName: billingDataFirstName,
              lastName: billingDataLastName,
              companyName,
              vatNumber,
            },
          },
        } = billingDataResponse

        assignWith(defaultBillingData, {
          bankAccountIBAN,
          bankAccountHolder,
          city,
          countryIso,
          zipCode,
          street,
          firstName: billingDataFirstName,
          lastName: billingDataLastName,
          companyName,
          vatNumber,
        }, (objValue, srcValue) => srcValue || objValue)
      }

      setIsFromAccount(ADDRESS_FIELDS.some(field => Boolean(defaultBillingData[field])))
      setIsIBANValid(Boolean(defaultBillingData.bankAccountIBAN))
      setBillingFormData({ ...defaultBillingData, isIBANValid: Boolean(defaultBillingData.bankAccountIBAN) })
    } catch {
      toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
    }
  }

  useEffect(() => {
    initSetup()
  }, [])

  // merged into the latest state: several changes can arrive before the next render (browser autofill)
  const handleBillingDataChange = (data) => {
    setBillingFormData(previous => ({ ...previous, ...data }))
  }

  return { billingFormData, isIBANValid, setIsIBANValid, isFromAccount, handleBillingDataChange }
}
